import { Avatar, Box, Divider, List, ListItem, ListItemText, Theme, createStyles, makeStyles } from '@material-ui/core';
import Client from '@spike/client-model';
import { ClientsStatus, fetchClientsThunk, searchClientsThunk } from '@spike/clients-action';
import { RootState } from 'store';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import { wbp, reduceResolution } from 'Theme';
import { Button } from 'components/UI';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface SearchClientsResultProps {
    onSelectClient?: (clientId?: number) => void;
    onAddClient: () => void;
    setShowNewPhone: (showNewPhone: boolean) => void;
    clientSearchText: string;
    marketplaceId: number;
}

const useStyles = makeStyles((theme: Theme) => {
    const baseAvatar = {
        [theme.breakpoints.down(wbp)]: {
            marginRight: `${reduceResolution(15)}px`,
            width: '25px',
            height: '25px'
        },
        [theme.breakpoints.up(wbp)]: {
            marginRight: '15px'
        }
    };

    return createStyles({
        container: { display: 'flex', flexDirection: 'column', height: '100%' },
        clientListContainer: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        },
        clientList: {
            height: '100%'
        },
        clientScroll: {
            width: '100%',
            height: '100%',
            overflowY: 'scroll',
            overflowX: 'hidden',
            [theme.breakpoints.down(wbp)]: {
                padding: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                padding: '15px'
            }
        },
        newClientButtonInList: {
            width: 'auto',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'right',
            paddingLeft: '35px',
            paddingRight: '35px',
            marginTop: '12px'
        },
        newClientButton: {
            'padding': '16px 16px',

            '& .MuiButton-startIcon': {
                '& svg': {
                    height: '16px',
                    width: '16px'
                }
            },

            '& .MuiButton-label': {
                fontSize: '16px'
            }
        },
        clientItemContainer: {
            'cursor': 'pointer',
            [theme.breakpoints.down(wbp)]: {
                padddingTop: `${reduceResolution(12)}px`,
                padddingBottom: `${reduceResolution(12)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingTop: '12px',
                paddingBottom: '12px'
            },
            '&:hover': {
                backgroundColor: '#FAFBFA'
            }
        },
        clientDivider: {
            backgroundColor: '#F1F1F1',
            margin: 'auto',
            width: '90%'
        },
        clientText: {
            fontWeight: 600
        },
        clientAvatar: {
            ...baseAvatar,
            backgroundColor: '#DCCCBB'
        },
        newPhoneNumber: {
            ...baseAvatar,
            backgroundColor: '#92B4A7'
        },
        loaderContainer: {
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1
        }
    });
});

const SearchClientsResult: FunctionComponent<SearchClientsResultProps> = props => {
    const classes = useStyles();

    const dispatch = useDispatch();

    const apiClientWrapper = useApiClientWrapper();

    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);

    const clientsPageSize = 30;

    const clientsList = useSelector<RootState, Array<Client>>(state => state.clients.list);

    const clientsPage = useSelector<RootState, number>(state => Number(state.clients.page) || 1);

    useNonInitialEffect(() => {
        fetchClients(Number(clientsPage), clientsPageSize);
    }, [props.clientSearchText, props.marketplaceId]);

    useNonInitialEffect(() => {
        if (clientsStatus === ClientsStatus.SaveSuccess) {
            fetchClients(clientsPage, clientsPageSize);
        }
    }, [clientsStatus]);

    const fetchClients = (page: number, pageSize: number) => {
        if (props.clientSearchText.length === 0) {
            dispatch(fetchClientsThunk(apiClientWrapper, page, pageSize, undefined, undefined, undefined, undefined));
        } else if (props.clientSearchText.length >= 3) {
            dispatch(
                searchClientsThunk(
                    apiClientWrapper,
                    props.clientSearchText,
                    page,
                    pageSize,
                    undefined,
                    undefined,
                    undefined
                )
            );
        }
    };

    const selectClientHandler = (clientId: number): void => {
        if (clientId) {
            props.setShowNewPhone(false);
            props.onSelectClient && props.onSelectClient(clientId);
        }
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.clientListContainer}>
                <Box
                    className={clsx(classes.clientList)}
                    sx={{
                        width: '100%',
                        bgcolor: 'background.paper'
                    }}
                >
                    <OverlayScrollbarsComponent className={clsx(classes.clientScroll)}>
                        <nav aria-label="client item">
                            <List>
                                <Box key="new-chat-phone-number">
                                    <ListItem
                                        className={classes.clientItemContainer}
                                        onClick={() => props.setShowNewPhone(true)}
                                    >
                                        <Avatar className={classes.newPhoneNumber}>
                                            <FontAwesomeIcon icon={faPlus} />
                                        </Avatar>
                                        <ListItemText
                                            className={classes.clientText}
                                            primary={'New phone number'}
                                        />
                                    </ListItem>
                                    <Divider className={classes.clientDivider} />
                                </Box>
                                {clientsList.map((client, index) => {
                                    return (
                                        <Box key={index + client.uuid}>
                                            <ListItem
                                                className={classes.clientItemContainer}
                                                onClick={() => selectClientHandler(client.id!)}
                                            >
                                                <Avatar className={classes.clientAvatar} />
                                                <ListItemText
                                                    className={classes.clientText}
                                                    primary={client.firstName + ' ' + client.lastName}
                                                />
                                            </ListItem>
                                            <Divider className={classes.clientDivider} />
                                        </Box>
                                    );
                                })}
                            </List>
                        </nav>
                    </OverlayScrollbarsComponent>
                </Box>
                <Box className={classes.newClientButtonInList}>
                    <Button
                        color="orange"
                        startIcon={faPlus}
                        label="Add client"
                        onClick={props.onAddClient}
                        className={classes.newClientButton}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default SearchClientsResult;
